import ImportDataIcon from "@mui/icons-material/ExitToAppRounded";
import ImportExportIcon from "@mui/icons-material/ImportExportRounded";
import ExportDataIcon from "@mui/icons-material/OutputRounded";
import { Button, Stack } from "@mui/material";
import { useCallback, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useParams } from "react-router";
import { withErrorHandling } from "@entrilia/shared/api/axiosHelper";
import type { MetricCellValueUpdate } from "@entrilia/shared/api/dataCollectionTypes";
import ActionsMenuButton from "@entrilia/shared/components/ActionsMenuButton";
import DataSubmissionStatusTag from "@entrilia/shared/components/dataCollection/DataSubmissionStatusTag";
import DataSubmissionForm from "@entrilia/shared/components/dataCollection/submissionForms/DataSubmissionForm";
import type { EditedFormValuesState } from "@entrilia/shared/components/dataCollection/submissionForms/editedFormValuesState";
import {
  addCellValueUpdatesAction,
  getFormUpdatesPerBlock,
  getInitialEditedFormValuesState,
  isEditedFormValuesStateEmpty,
} from "@entrilia/shared/components/dataCollection/submissionForms/editedFormValuesState";
import { ImportErrorsAlert } from "@entrilia/shared/components/dataCollection/submissionForms/portfolioMonitoring/ImportErrorsAlert";
import ImportSubmissionBlockToExcelDialog from "@entrilia/shared/components/dataCollection/submissionForms/portfolioMonitoring/ImportSubmissionBlockToExcelDialog";
import DataLoadingFailed from "@entrilia/shared/components/DataLoadingFailed";
import InlineLoader from "@entrilia/shared/components/inlineLoader/InlineLoader";
import { useNotificationContext } from "@entrilia/shared/contexts/NotificationContext";
import useFetch from "@entrilia/shared/hooks/useFetch";
import useToggleState from "@entrilia/shared/hooks/useToggleState";
import { logError } from "@entrilia/shared/logging";
import { downloadFileFromUrl } from "@entrilia/shared/services/downloadFile";
import { defined } from "@entrilia/shared/utilities/typeHelper";
import { api } from "../../../api/client";
import type {
  DataCollectionSubmissionDetails,
  ImportSubmissionBlockInputResponse,
} from "../../../api/types/dataCollectionTypes";
import useClientContext from "../../../contexts/useClientContext";
import useClientPageTitle from "../../../contexts/useClientPageTitle";
import { pageRoutes } from "../../../routes";
import { entityPermissionsSelector } from "../../../store/state/user/selectors";
import PageHeader from "../../common/PageHeader";
import SubmitConfirmationDialog from "./SubmitConfirmationDialog";

const saveDataSubmissionLayoutBlockInput = withErrorHandling(api.dataCollection.saveDataSubmissionLayoutBlockInput);
const exportToExcel = withErrorHandling(api.dataCollection.exportSubmissionBlockInputToExcel);

const DataSubmissionPage = () => {
  useClientPageTitle("Data Submission");

  const { id } = useParams();
  const { clientCode } = useClientContext();
  const entityPermissions = useSelector(entityPermissionsSelector);
  const { sendNotificationError } = useNotificationContext();

  const [formRenderKey, toggleFormRenderKey] = useToggleState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [editedFormValues, setEditedFormValues] = useState<EditedFormValuesState>(() =>
    getInitialEditedFormValuesState()
  );
  const [showSubmitConfirmationDialog, setShowSubmitConfirmationDialog] = useState(false);
  const [showImportDataDialog, setShowImportDataDialog] = useState(false);
  const [importErrors, setImportErrors] = useState<string[]>([]);

  const getDataSubmissionDetails = useCallback(
    (signal?: AbortSignal) => api.dataCollection.getDataSubmissionDetails(defined(id), signal),
    [id]
  );

  const [
    dataSubmission,
    fetchSubmissionError,
    { isFetching: isFetchingSubmission, setData: setDataSubmission, fetch: fetchSubmission },
  ] = useFetch(getDataSubmissionDetails);

  const handleBlockCellValuesEdit = useCallback((blockId: string, cellValueUpdates: MetricCellValueUpdate[]) => {
    setEditedFormValues(addCellValueUpdatesAction(blockId, cellValueUpdates));
  }, []);

  const hasPermissionsToEdit = useMemo(
    () =>
      entityPermissions.some(
        (p) =>
          !!dataSubmission?.recipientObjectId &&
          p.objectId === dataSubmission.recipientObjectId &&
          p.role === "Portfolio_Monitoring_Portal_Member"
      ),
    [dataSubmission, entityPermissions]
  );

  const isSubmissionEditable = useMemo(
    () => hasPermissionsToEdit && dataSubmission?.status === "Pending",
    [dataSubmission?.status, hasPermissionsToEdit]
  );

  if (fetchSubmissionError) {
    logError(fetchSubmissionError, "[DataSubmissionPage] getDataSubmissionDetails");
    return <DataLoadingFailed title="Could not load data submission details" />;
  }

  if (isFetchingSubmission || dataSubmission === undefined) {
    return <InlineLoader />;
  }

  const handleSubmitClick = () => {
    setShowSubmitConfirmationDialog(true);
  };

  const handleSaveClick = async () => {
    const formUpdates = getFormUpdatesPerBlock(editedFormValues);
    let updatedSubmission: DataCollectionSubmissionDetails | undefined = undefined;

    setIsSaving(true);

    for (const [blockId, payload] of formUpdates) {
      const [resp, error] = await saveDataSubmissionLayoutBlockInput(defined(id), blockId, payload);

      if (error) {
        logError(error, `[DataSubmissionPage] saveDataSubmissionLayoutBlockInput (${id}:${blockId})`);
        sendNotificationError(error.message);
        continue;
      }

      updatedSubmission = resp;
    }

    setIsSaving(false);

    if (updatedSubmission) {
      setEditedFormValues(getInitialEditedFormValuesState());
      setDataSubmission(updatedSubmission);
      toggleFormRenderKey();
    }
  };

  const handleSubmitted = () => {
    setShowSubmitConfirmationDialog(false);
    fetchSubmission();
  };

  const handleImport = (importResp: ImportSubmissionBlockInputResponse) => {
    setShowImportDataDialog(false);

    if (importResp.errors.length > 0) {
      setImportErrors(importResp.errors);
    }

    if (importResp.updatedSubmission !== undefined) {
      setDataSubmission(importResp.updatedSubmission);
      toggleFormRenderKey();
    }
  };

  const handleCancelClick = () => {
    setEditedFormValues(getInitialEditedFormValuesState());
    toggleFormRenderKey();
  };

  const handleImportDataClick = () => {
    setShowImportDataDialog(true);
  };

  const handleExportDataClick = async () => {
    if (!importableBlock) {
      return;
    }

    setIsExporting(true);
    const [fileDownloadInfo, error] = await exportToExcel(dataSubmission.id, importableBlock.blockId);
    setIsExporting(false);

    if (error) {
      logError(error, "[DataSubmissionPage] exportSubmissionBlockInputToExcel");
      sendNotificationError("Failed to export submission form");
      return;
    }

    await downloadFileFromUrl(fileDownloadInfo.downloadUrl);
  };

  const importableBlock = dataSubmission.blockContents.find((block) => block.capabilities.includes("ExcelImport"));

  const isSaveDisabled = isEditedFormValuesStateEmpty(editedFormValues);
  const isSubmitDisabled = !isSaveDisabled || isSaving || !dataSubmission.canBeSubmitted;
  const isImportDisabled = !isSaveDisabled || isSaving;

  return (
    <>
      <PageHeader
        title={dataSubmission.requestName}
        titleAdornment={<DataSubmissionStatusTag status={dataSubmission.status} />}
        subtitle="Data Submission"
        backButtonLink={`/${clientCode}/${pageRoutes.dataCollection}`}
      >
        {isSubmissionEditable && (
          <Stack direction="row" spacing={1}>
            {importableBlock !== undefined && (
              <ActionsMenuButton
                text="Import/Export"
                variant="outlined"
                color="secondary"
                icon={<ImportExportIcon />}
                disabled={isImportDisabled}
                loading={isExporting}
                items={[
                  { label: "Import Data", icon: <ImportDataIcon />, onClick: handleImportDataClick },
                  { label: "Export Submission Form", icon: <ExportDataIcon />, onClick: handleExportDataClick },
                ]}
              />
            )}
            <Button variant="text" color="secondary" disabled={isSaveDisabled} onClick={handleCancelClick}>
              Cancel
            </Button>
            <Button variant="contained" loading={isSaving} disabled={isSaveDisabled} onClick={handleSaveClick}>
              Save
            </Button>
            <Button variant="outlined" disabled={isSubmitDisabled} onClick={handleSubmitClick}>
              Submit
            </Button>
          </Stack>
        )}
      </PageHeader>

      <ImportErrorsAlert errors={importErrors} onClose={() => setImportErrors([])} />

      <DataSubmissionForm
        key={formRenderKey.toString()}
        recipientObjectId={dataSubmission.recipientObjectId}
        layout={dataSubmission.layout}
        blockContents={dataSubmission.blockContents}
        isSubmissionEditable={isSubmissionEditable}
        metricExtensionsService={{
          searchObjectMetricExtensions: api.dataCollection.searchObjectMetricExtensions,
          createObjectMetricExtensions: api.dataCollection.createObjectMetricExtensions,
        }}
        onBlockCellValuesEdit={handleBlockCellValuesEdit}
      />

      <SubmitConfirmationDialog
        submission={dataSubmission}
        open={showSubmitConfirmationDialog}
        onClose={() => setShowSubmitConfirmationDialog(false)}
        onSubmitted={handleSubmitted}
      />

      {showImportDataDialog && importableBlock !== undefined && (
        <ImportSubmissionBlockToExcelDialog<DataCollectionSubmissionDetails>
          submissionImportService={{
            exportSubmissionBlockInputToExcel: api.dataCollection.exportSubmissionBlockInputToExcel,
            importSubmissionBlockInputFromExcel: api.dataCollection.importSubmissionBlockInputFromExcel,
          }}
          submissionId={dataSubmission.id}
          blockId={importableBlock.blockId}
          onClose={() => setShowImportDataDialog(false)}
          onImport={handleImport}
        />
      )}
    </>
  );
};

export default DataSubmissionPage;
